import Storage from 'utils/localStorage/Storage';

const SESSION_EXPIRY = Storage.DAYS * 7;
const SUPERCHAT_CONFIG_EXPIRY = Storage.DAYS * 1;
const ANONYMOUS_EXPIRY = Storage.DAYS * 7;
const FOLLOW_UPS_EXPIRY = Storage.DAYS * 7;

const KEYS = {
    GENAI_SESSION_KEY: 'GENAI_SESSION',
    GENAI_ANONYMOUS_ID_KEY: 'GENAI_ANONYMOUS_ID',
    SUPERCHAT_CONFIG_KEY: 'SUPERCHAT_CONFIG',
    GENAI_LAST_FOLLOW_UPS_KEY: 'GENAI_LAST_FOLLOW_UPS',
    SUPERCHAT_REFERRER_KEY: 'SUPERCHAT_REFERRER'
};

const setGENAISession = dataSession => {
    const session = JSON.stringify(dataSession);
    Storage.local.setItem(KEYS.GENAI_SESSION_KEY, session, SESSION_EXPIRY);
};

const setSuperChatConfig = config => {
    try {
        Storage.local.setItem(KEYS.SUPERCHAT_CONFIG_KEY, JSON.stringify(config), SUPERCHAT_CONFIG_EXPIRY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error creating Super Chat Config in LocalStorage');
    }
};

const setGENAIAnonymousId = (anonymousId, associatedBiId = null) => {
    const anonymousData = {
        anonymousId,
        associatedBiId
    };
    Storage.local.setItem(KEYS.GENAI_ANONYMOUS_ID_KEY, JSON.stringify(anonymousData), ANONYMOUS_EXPIRY);
};

const setLastFollowUps = (sessionId, followUps) => {
    if (!sessionId || !Array.isArray(followUps)) {
        return;
    }

    try {
        const payload = JSON.stringify({
            sessionId,
            followUps
        });
        Storage.local.setItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY, payload, FOLLOW_UPS_EXPIRY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error storing last follow ups in LocalStorage', error);
    }
};

const getGENAISession = () => {
    const existingSession = Storage.local.getItem(KEYS.GENAI_SESSION_KEY);

    if (!existingSession) {
        return null;
    }

    return JSON.parse(existingSession);
};

const getSuperChatConfig = () => {
    const existingConfig = Storage.local.getItem(KEYS.SUPERCHAT_CONFIG_KEY);

    try {
        return JSON.parse(existingConfig);
    } catch (error) {
        return null;
    }
};

const getLastFollowUps = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY);

    if (!storedValue) {
        return null;
    }

    try {
        return JSON.parse(storedValue);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error parsing stored follow ups from LocalStorage', error);
        removeLastFollowUps();

        return null;
    }
};

const getGENAIAnonymousId = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_ANONYMOUS_ID_KEY);

    if (!storedValue) {
        return null;
    }

    // Handle migration from old string format to new object format
    if (typeof storedValue === 'string' && storedValue.indexOf('{') !== 0) {
        // Old format: plain string anonymousId
        return storedValue;
    }

    try {
        const parsed = typeof storedValue === 'string' ? JSON.parse(storedValue) : storedValue;

        return parsed.anonymousId || null;
    } catch (error) {
        // If parsing fails, assume it's the old string format
        return storedValue;
    }
};

const getAssociatedBiId = () => {
    const storedValue = Storage.local.getItem(KEYS.GENAI_ANONYMOUS_ID_KEY);

    if (!storedValue) {
        return null;
    }

    // Old format doesn't have BiId association
    if (typeof storedValue === 'string' && storedValue.indexOf('{') !== 0) {
        return null;
    }

    try {
        const parsed = typeof storedValue === 'string' ? JSON.parse(storedValue) : storedValue;

        return parsed.associatedBiId || null;
    } catch (error) {
        return null;
    }
};

const removeLastFollowUps = () => {
    Storage.local.removeItem(KEYS.GENAI_LAST_FOLLOW_UPS_KEY);
};

const removeSuperChatConfig = () => {
    try {
        Storage.local.removeItem(KEYS.SUPERCHAT_CONFIG_KEY);
    } catch (error) {
        Sephora.logger.error('SuperChat: Error removing Super Chat Config from LocalStorage');
    }
};

const removeGENAISession = () => {
    Storage.local.removeItem(KEYS.GENAI_SESSION_KEY);
    removeLastFollowUps();
};

const removeGENAIAnonymousId = () => {
    Storage.local.removeItem(KEYS.GENAI_ANONYMOUS_ID_KEY);
};

const setSuperChatReferrer = referrer => {
    if (referrer) {
        Storage.session.setItem(KEYS.SUPERCHAT_REFERRER_KEY, referrer);
    }
};

const getSuperChatReferrer = () => {
    return Storage.session.getItem(KEYS.SUPERCHAT_REFERRER_KEY) || null;
};

// Cleans up when no longer needed to prevent stale data from affecting future analytics.
// referrer context should persist across page navigations but clear when the browser session ends.
const removeSuperChatReferrer = () => {
    Storage.session.removeItem(KEYS.SUPERCHAT_REFERRER_KEY);
};

export {
    setGENAISession,
    setGENAIAnonymousId,
    setLastFollowUps,
    setSuperChatConfig,
    getGENAISession,
    getSuperChatConfig,
    getLastFollowUps,
    removeSuperChatConfig,
    getGENAIAnonymousId,
    getAssociatedBiId,
    removeGENAISession,
    removeGENAIAnonymousId,
    removeLastFollowUps,
    setSuperChatReferrer,
    getSuperChatReferrer,
    removeSuperChatReferrer,
    KEYS
};
